import React, { useState, useEffect } from 'react';

const TYPES = [
  { value: 'General feedback / suggestion', emoji: '💬' },
  { value: 'Bug report',                    emoji: '🐛' },
  { value: 'New feature idea',              emoji: '💡' },
  { value: 'Puzzle feedback',               emoji: '⚽' },
  { value: 'Other',                         emoji: '📝' },
];

function FeedbackModal({ onClose }) {
  const [type,       setType]       = useState('');
  const [message,    setMessage]    = useState('');
  const [_hp,        setHp]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState('');

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = async () => {
    if (_hp) return;
    if (!type) { setError('Please select a feedback type.'); return; }
    if (message.trim().length < 5) { setError('Please write a message.'); return; }

    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, message: message.trim(), _hp }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Submission failed');
      }
      setSubmitted(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-surface2 border border-border rounded-2xl w-full max-w-sm shadow-2xl"
        style={{ animation: 'popIn 0.2s ease-out' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-border">
          <div>
            <div className="font-semibold text-white">Share your thoughts</div>
            <div className="text-xs text-dimText mt-0.5">We read every message.</div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full border border-border text-dimText hover:border-accent hover:text-white transition-colors text-xs"
          >✕</button>
        </div>

        {!submitted ? (
          <div className="px-5 py-4 space-y-3">
            {/* Type selector */}
            <div>
              <label className="text-xs text-dimText mb-2 block">What's this about?</label>
              <div className="flex flex-wrap gap-1.5">
                {TYPES.map(t => (
                  <button
                    key={t.value}
                    onClick={() => setType(t.value)}
                    className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border transition-colors
                      ${type === t.value
                        ? 'border-accent bg-accent/10 text-accent'
                        : 'border-border text-dimText hover:border-accent/50 hover:text-white'
                      }`}
                  >
                    <span>{t.emoji}</span>
                    <span>{t.value}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="text-xs text-dimText mb-1.5 block">
                Message <span className="text-wrong">*</span>
              </label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={
                  type === 'Bug report'       ? 'Describe what happened and how to reproduce it…' :
                  type === 'New feature idea' ? 'What would you like to see added?…' :
                  type === 'Puzzle feedback'  ? 'Was a puzzle too hard, too easy, or incorrect?…' :
                  "What's on your mind?…"
                }
                rows={4}
                maxLength={1000}
                autoFocus
                className="w-full bg-surface border border-border rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-dimText outline-none focus:border-accent transition-colors resize-none"
              />
              <div className="text-right text-[10px] text-dimText mt-0.5">{message.length}/1000</div>
            </div>

            {/* Honeypot */}
            <input
              type="text"
              value={_hp}
              onChange={e => setHp(e.target.value)}
              tabIndex={-1}
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
            />

            {error && <p className="text-wrong text-xs">{error}</p>}

            <div className="flex gap-2 pt-1">
              <button
                onClick={handleSubmit}
                disabled={submitting || !type || message.trim().length < 5}
                className="flex-1 bg-accent text-bg text-sm font-semibold py-2.5 rounded-xl hover:bg-accent/90 disabled:opacity-40 transition-colors"
              >
                {submitting ? 'Sending…' : 'Send feedback'}
              </button>
              <button
                onClick={onClose}
                className="flex-1 border border-border text-dimText text-sm py-2.5 rounded-xl hover:border-accent hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-8 text-center">
            <div className="text-4xl mb-3">🙏</div>
            <div className="font-semibold text-white mb-1">Thanks for your feedback!</div>
            <div className="text-sm text-dimText mb-5">We genuinely appreciate it.</div>
            <button
              onClick={onClose}
              className="bg-accent text-bg text-sm font-semibold px-6 py-2.5 rounded-xl hover:bg-accent/90 transition-colors"
            >
              Back to game
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Trigger button shown in page footer ───────────────────────────────────────
export default function FeedbackForm() {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <div className="border-t border-border mt-8 pt-6 pb-4">
        <button
          onClick={() => setShowModal(true)}
          className="w-full flex items-center justify-center gap-2 text-xs text-dimText hover:text-white border border-border hover:border-accent rounded-xl px-4 py-3 transition-colors"
        >
          <span>🙋</span>
          <span>Have a suggestion or spotted something? Let us know</span>
          <span className="ml-auto opacity-50">→</span>
        </button>
      </div>

      {showModal && <FeedbackModal onClose={() => setShowModal(false)} />}
    </>
  );
}
